import { NotificationDto, NotificationSeverity } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Info, AlertTriangle, AlertCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactNode } from "react";
import { formatViennaDateTime } from "@/lib/vienna-time";

function isExternalUrl(url: string): boolean {
    try {
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return true;
        }
        if (url.includes("://")) {
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

export interface NotificationCardProps {
    notification: NotificationDto;
    onActionClick?: (notification: NotificationDto) => void;
    showTimestamp?: boolean;
    children?: ReactNode;
}

export function NotificationCard({
    notification,
    onActionClick,
    showTimestamp = true,
    children,
}: NotificationCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getSeverityConfig = (severity: NotificationSeverity) => {
        switch (severity) {
            case NotificationSeverity.Info:
                return {
                    icon: <Info className="h-5 w-5" />,
                    badgeClass:
                        "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100",
                    label: t("notifications.severity.info"),
                };
            case NotificationSeverity.Warning:
                return {
                    icon: <AlertTriangle className="h-5 w-5" />,
                    badgeClass:
                        "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-100",
                    label: t("notifications.severity.warning"),
                };
            case NotificationSeverity.Critical:
                return {
                    icon: <AlertCircle className="h-5 w-5" />,
                    badgeClass:
                        "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-100",
                    label: t("notifications.severity.critical"),
                };
        }
    };

    const handleActionClick = () => {
        if (!notification.actionUrl) return;

        const isExternal = isExternalUrl(notification.actionUrl);

        if (notification.openInNewTab) {
            if (isExternal) {
                window.open(
                    notification.actionUrl,
                    "_blank",
                    "noopener,noreferrer",
                );
            } else {
                const fullUrl = window.location.origin + notification.actionUrl;
                window.open(fullUrl, "_blank", "noopener,noreferrer");
            }
        } else {
            if (isExternal) {
                window.location.href = notification.actionUrl;
            } else {
                void navigate(notification.actionUrl);
            }
        }

        if (onActionClick) {
            onActionClick(notification);
        }
    };

    const severityConfig = getSeverityConfig(notification.severity);

    return (
        <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    {severityConfig.icon}
                    <Badge className={severityConfig.badgeClass}>
                        {severityConfig.label}
                    </Badge>
                </div>
                {showTimestamp ? (
                    <span className="text-xs text-muted-foreground">
                        {formatViennaDateTime(notification.createdAt)}
                    </span>
                ) : null}
            </div>

            <h4 className="font-semibold break-words">{notification.title}</h4>
            <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {notification.message}
            </p>

            {notification.actionUrl ? (
                <Button
                    className="p-0 h-auto text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                    onClick={handleActionClick}
                    size="sm"
                    variant="link"
                >
                    {t("notifications.view-details")}
                    <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
            ) : null}

            {children}
        </div>
    );
}
